import { useEffect, useRef, useState } from "react";
import { Button } from "@/shared/ui/button.tsx"
import { Card } from "@/shared/ui/card.tsx"
import { Separator } from "@/shared/ui/separator.tsx"
import { X, Check, Loader2, Undo2 } from "lucide-react";
import { Textarea } from "@/shared/ui/textarea.tsx";
import { Label } from "@/shared/ui/label.tsx";
import { ConfigFieldArgumentType, ConfigFieldType, useMutateField } from "../api";
import DeleteFieldDialog from "./DeleteFieldDialog";


type Props = {
  field: ConfigFieldType
}

const ConfigInputs = ({ field }: Props) => {

  const { mutateField, status } = useMutateField()

  const [error, setError] = useState<string | undefined>(undefined)

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto"; // Reset height to auto to calculate new height
      textarea.style.height = `${textarea.scrollHeight}px`; // Set new height based on content
    }
  }, [field]);

  const saveConfigHandler = () => {
    if (textareaRef.current && textareaRef.current.value.length > 0) {
      setError(undefined)
      const newData: ConfigFieldArgumentType = { name: field.name, endpoint: textareaRef.current.value, description: field.description }
      mutateField(newData)
      // console.log(newData)
    }

    if (textareaRef.current && textareaRef.current.value.length === 0) {
      setError("Field is required")
    }
  }

  const resetConfigHandler = () => {
    if (textareaRef.current) {
      textareaRef.current.value = field.endpoint
    }
  }

  return (
    <Card className="w-full px-5 py-1 flex flex-col">
      <div className="w-full py-3 flex items-center justify-between">
        <Label className="capitalize font-poppins font-[500] text-gray-600">
          {field.name.replace(/(^\w|_\w)/g, match => match.replace('_', ' '))}
        </Label>
        <div className="flex items-center gap-5">
          <DeleteFieldDialog
            fieldName={field.name}
            trigger={
              <Button
                variant="outline" size="icon" type="button" className="h-7 hover:bg-white" >
                <X />
              </Button>}
          />
          <Button
            onClick={resetConfigHandler}
            variant="outline" size="icon" type="button" className="h-7 hover:bg-white" >
            <Undo2 />
          </Button>
          <Button
            onClick={saveConfigHandler}
            variant="outline" size="icon" type="button" className="h-7 hover:bg-white">
            {status === "pending" ? <Loader2 className="animate-spin" /> : <Check />}
          </Button>
        </div>
      </div>
      <Separator className="px-4" />
      <div className="flex py-1 gap-2 items-center">
        <Textarea
          defaultValue={field.endpoint}
          ref={textareaRef}
          className="h-fit outline-none border-none resize-none shadow-none disabled:cursor-text"
        />
      </div>
      {error &&
        <p className="font-medium font-poppins tracking-wide text-red-600 py-2 text-sm">{error}</p>
      }
    </Card>
  )
}

export default ConfigInputs;
